import {
  Bell,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Clock3,
  LayoutGrid,
  Search,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourse, getPublicAcademyBySlug, getStudentCourseSignups, getTenant } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { getHostnameContext } from '../../utils/hostname';
import defaultCourseImage from '../../assets/newimage29-optimized.webp';
import { StudentSidebar } from './StudentSidebar';

const TYPE_LABELS = {
  live_online: 'Live online tutoring',
  in_person: 'In-person classes',
  hybrid: 'Hybrid learning',
  self_paced: 'Self-paced digital course',
};

function courseProgress(record) {
  const value = Number(record.progressPercentage ?? record.progress ?? 0);
  return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
}

function isActiveCourse(record) {
  return ['active', 'admitted', 'approved', 'enrolled'].includes(String(record.status || '').toLowerCase());
}

export function StudentCoursesPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const hostnameContext = useMemo(() => getHostnameContext(), []);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [academyName, setAcademyName] = useState('Student workspace');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user?.uid) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const signups = await getStudentCourseSignups(user.uid);
        let scoped = signups;
        if (hostnameContext.tenantSlug) {
          const academy = await getPublicAcademyBySlug(hostnameContext.tenantSlug);
          scoped = academy ? signups.filter((signup) => signup.tenantId === academy.id) : [];
        }
        const records = await Promise.all(scoped.map(async (signup) => {
          const tenantId = signup.tenantId || signup.ref?.parent?.parent?.id || '';
          const [tenant, course] = await Promise.all([
            tenantId ? getTenant(tenantId) : Promise.resolve(null),
            tenantId && signup.courseId ? getCourse(tenantId, signup.courseId) : Promise.resolve(null),
          ]);
          return { ...signup, tenantId, tenant, course };
        }));
        const uniqueCourses = records
          .filter((record) => record.course)
          .filter((record, index, list) => list.findIndex((item) => `${item.tenantId}-${item.course.id}` === `${record.tenantId}-${record.course.id}`) === index);
        if (mounted) {
          setCourses(uniqueCourses);
          setAcademyName(uniqueCourses[0]?.tenant?.name || 'My learning space');
          setLoading(false);
        }
      } catch (error) {
        console.error('Unable to load student courses', error);
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [hostnameContext.tenantSlug, user?.uid]);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  if (authLoading) return <LoadingState label="Loading student portal..." />;
  if (!user) return <Navigate to="/student/login" replace />;

  const normalizedQuery = query.trim().toLowerCase();
  const filteredCourses = normalizedQuery ? courses.filter((record) => {
    const course = record.course;
    return [course.name, course.description, TYPE_LABELS[course.courseType], record.tenant?.name]
      .some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
  }) : courses;
  const activeCourses = courses.filter(isActiveCourse).length;
  const averageProgress = courses.length
    ? Math.round(courses.reduce((sum, record) => sum + courseProgress(record), 0) / courses.length)
    : 0;
  const firstName = String(user.displayName || user.email || 'Student').split(/[ @]/)[0];
  const dateLabel = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date());

  return (
    <main className="student-portal student-courses-page">
      <StudentSidebar academyName={academyName} onLogout={onLogout} />

      <div className="student-courses-shell">
        <section className="student-courses-workspace">
          <header className="student-courses-page-header">
            <div>
              <span>Welcome back, {firstName}</span>
              <h1>My Courses</h1>
              <p>{dateLabel}</p>
            </div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications">
              <Bell size={19} strokeWidth={1.7} />
              <i />
            </button>
          </header>

          <div className="student-courses-toolbar">
            <label><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search your courses" /></label>
            <div><LayoutGrid size={16} /><span>{filteredCourses.length} course{filteredCourses.length === 1 ? '' : 's'}</span></div>
          </div>

          <div className="student-courses-section-heading">
            <div><h2>Your learning library</h2><p>Continue learning from where you last stopped.</p></div>
            <span>All courses</span>
          </div>

          {loading ? (
            <div className="student-courses-inline-loading"><span /><p>Loading your courses...</p></div>
          ) : filteredCourses.length ? (
            <div className="student-courses-library">
              {filteredCourses.map((record, index) => {
                const course = record.course;
                const progress = courseProgress(record);
                const coursePath = `/student/courses/${record.tenant?.id || record.tenantId}/${course.id}`;
                return (
                  <article className={`student-library-card student-library-card--${(index % 3) + 1}`} key={`${record.tenantId}-${course.id}`}>
                    <Link className="student-library-image" to={coursePath}>
                      <img src={course.imageURL || course.imageUrl || defaultCourseImage} alt="" />
                      <span>{TYPE_LABELS[course.courseType] || 'Learning course'}</span>
                    </Link>
                    <div className="student-library-card-body">
                      <div className="student-library-school"><span>{record.tenant?.name || academyName}</span><i>{String(record.status || 'enrolled').replace(/_/g, ' ')}</i></div>
                      <h3>{course.name}</h3>
                      <p>{course.description || 'Continue your learning journey with this course.'}</p>
                      <div className="student-library-meta">
                        <span><Clock3 size={14} />{course.duration || 'Flexible duration'}</span>
                        <span><BookOpen size={14} />{record.intakeLabel || 'Open intake'}</span>
                      </div>
                      <div className="student-library-progress">
                        <div><span>Course progress</span><b>{progress}%</b></div>
                        <i><span style={{ width: `${progress}%` }} /></i>
                      </div>
                      <Link className="student-library-open" to={coursePath}>Open course <ChevronRight size={16} /></Link>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="student-courses-empty">
              <BookOpen size={25} />
              <h2>{query ? 'No matching courses' : 'Your courses will appear here'}</h2>
              <p>{query ? 'Try a different course name or learning mode.' : 'Once your academy confirms an enrollment, you can start learning from this page.'}</p>
              {query ? <button type="button" onClick={() => setQuery('')}>Clear search</button> : null}
            </div>
          )}
        </section>

        <aside className="student-courses-summary">
          <div className="student-courses-summary-heading"><h2>Learning Summary</h2><Sparkles size={17} /></div>
          <div className="student-courses-summary-ring" style={{ '--course-progress': `${averageProgress * 3.6}deg` }}>
            <div><strong>{averageProgress}%</strong><small>Overall progress</small></div>
          </div>
          <div className="student-courses-summary-stats">
            <div><span className="is-violet"><BookOpen size={17} /></span><small>Total courses</small><strong>{courses.length}</strong></div>
            <div><span className="is-blue"><CheckCircle2 size={17} /></span><small>Active courses</small><strong>{activeCourses}</strong></div>
          </div>
          <div className="student-learning-tip">
            <span><Sparkles size={16} /></span>
            <small>Learning tip</small>
            <h3>Small steps create lasting progress.</h3>
            <p>Pick one course and complete your next learning activity today.</p>
          </div>
          <Link className="student-courses-dashboard-link" to="/student/portal">Back to dashboard <ChevronRight size={16} /></Link>
        </aside>
      </div>
    </main>
  );
}
